import type { PermissionInfo } from '@/gen/cortado/v1/settings_pb'

/** Stored key of the wildcard "Full access" permission — implies every other key. */
export const ADMIN_PERMISSION_KEY = 'Admin'

export type PermissionLevel = 'none' | 'view' | 'manage'

export interface PermissionCategory {
  category: string
  /** Present when the domain has a "Manage"/"Full" level. */
  managePermission?: PermissionInfo
  /** Present when the domain has a "View" level. */
  viewPermission?: PermissionInfo
}

/**
 * Groups assignable permissions by their `category`, excluding the Admin wildcard.
 * Category order follows the order permissions are returned by ListPermissions, so the
 * matrix renders in the server's declared order with no frontend-side sorting.
 */
export function getPermissionCategories(permissions: PermissionInfo[]): PermissionCategory[] {
  const byCategory = new Map<string, PermissionInfo[]>()

  for (const permission of permissions) {
    if (permission.key === ADMIN_PERMISSION_KEY) continue
    const existing = byCategory.get(permission.category)
    if (existing) {
      existing.push(permission)
    } else {
      byCategory.set(permission.category, [permission])
    }
  }

  return Array.from(byCategory.entries()).map(([category, perms]) => ({
    category,
    managePermission: perms.find((p) => p.manageLevel),
    viewPermission: perms.find((p) => !p.manageLevel),
  }))
}

/** Reads the current access level for a category from a selected-key set. */
export function getCategoryLevel(
  category: PermissionCategory,
  selectedKeys: string[]
): PermissionLevel {
  if (category.managePermission && selectedKeys.includes(category.managePermission.key)) {
    return 'manage'
  }
  if (category.viewPermission && selectedKeys.includes(category.viewPermission.key)) {
    return 'view'
  }
  return 'none'
}

/**
 * Returns the updated permission-key set after setting a category's access level.
 * Only ever stores the single key for the requested level — "manage" implies "view"
 * by construction (the paired view key is never also written), so `Manage ⇒ View` is
 * encoded in this function's shape rather than validated after the fact.
 */
export function setCategoryLevel(
  category: PermissionCategory,
  selectedKeys: string[],
  level: PermissionLevel
): string[] {
  const next = selectedKeys.filter(
    (key) => key !== category.managePermission?.key && key !== category.viewPermission?.key
  )

  if (level === 'manage' && category.managePermission) {
    next.push(category.managePermission.key)
  } else if (level === 'view' && category.viewPermission) {
    next.push(category.viewPermission.key)
  }

  return next
}

export function isFullAccess(selectedKeys: string[]): boolean {
  return selectedKeys.includes(ADMIN_PERMISSION_KEY)
}

/**
 * Human-readable summary of a role's effective access, e.g. "Full access",
 * "No access", "Manage: Invoices, Payments", or
 * "Manage: Invoices; View only: Contacts, Reporting".
 */
export function summarizePermissions(permissions: PermissionInfo[], selectedKeys: string[]): string {
  if (isFullAccess(selectedKeys)) return 'Full access'

  const manageCategories: string[] = []
  const viewOnlyCategories: string[] = []

  for (const category of getPermissionCategories(permissions)) {
    const level = getCategoryLevel(category, selectedKeys)
    if (level === 'manage') manageCategories.push(category.category)
    else if (level === 'view') viewOnlyCategories.push(category.category)
  }

  if (manageCategories.length === 0 && viewOnlyCategories.length === 0) {
    return 'No access'
  }

  const parts: string[] = []
  if (manageCategories.length > 0) parts.push(`Manage: ${manageCategories.join(', ')}`)
  if (viewOnlyCategories.length > 0) parts.push(`View only: ${viewOnlyCategories.join(', ')}`)
  return parts.join('; ')
}

export interface RolePreset {
  id: string
  label: string
  description: string
  /** Canonical (non-redundant) permission keys — one key per granted domain. */
  permissionKeys: string[]
}

/**
 * Static role-editor presets (per the RBAC rollout plan §4.3). These pre-fill the
 * matrix on the create flow and stay fully editable afterward — they are frontend
 * templates, not stored roles.
 */
export const ROLE_PRESETS: RolePreset[] = [
  {
    id: 'manager',
    label: 'Manager',
    description: 'Everything except Team, Settings, and Billing.',
    permissionKeys: [
      'ContactsManage',
      'LeadsManage',
      'EventsManage',
      'QuotesManage',
      'InvoicesManage',
      'PaymentsManage',
      'CatalogManage',
      'CommsManage',
      'AutomationsManage',
      'ReportingView',
    ],
  },
  {
    id: 'event-staff',
    label: 'Event Staff',
    description:
      'Manages events and bookings, with read access to leads, contacts, quotes, and the catalog (needed for the event-type and line-item pickers).',
    permissionKeys: ['EventsManage', 'LeadsView', 'ContactsView', 'QuotesView', 'CatalogView'],
  },
  {
    id: 'bookkeeper',
    label: 'Bookkeeper',
    description:
      'Manages invoices and payments, with reporting and contact visibility and read access to the catalog (needed for the invoice line-item picker).',
    permissionKeys: ['InvoicesManage', 'PaymentsManage', 'ReportingView', 'ContactsView', 'CatalogView'],
  },
]

/**
 * Resolves a preset's keys against the live permission list, dropping any keys the
 * backend no longer serves. Presets are static frontend templates, so this guards
 * against drift rather than assuming the hardcoded keys are always still assignable.
 */
export function applyPreset(preset: RolePreset, permissions: PermissionInfo[]): string[] {
  const assignableKeys = new Set(permissions.map((p) => p.key))
  return preset.permissionKeys.filter((key) => assignableKeys.has(key))
}
